import AppTableControl from '@/components/AppTableControl';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import { Space, Table } from 'antd';
import React, { useMemo, useState } from 'react';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getFreePlay, getFreePlayOverview } from '@/services/customer_care.service';
import dayjs from 'dayjs';
import { formatDate, columnSorter, formatNumber } from '@/utils/formatters';
import { getActiveBadge } from '@/utils/getActiveBadge';

const overviewCols = [
  {
    title: 'Date',
    dataIndex: 'report_date',
    key: 'report_date',
    fixed: 'left',
    width: 100,
    render: (date) => formatDate(date, 'DD/MM/YYYY'),
    sorter: columnSorter('date'),
  },
  {
    title: 'Total Plays',
    dataIndex: 'total_play',
    key: 'total_play',
    sorter: columnSorter('total_play'),
    render: (text) => (text ? formatNumber(text) : '0'),
  },
  {
    title: 'Wins',
    dataIndex: 'total_win',
    key: 'total_win',
    sorter: columnSorter('total_win'),
    render: (text) => (text ? formatNumber(text) : '0'),
  },
  {
    title: 'Loses',
    dataIndex: 'total_losses',
    key: 'total_losses',
    sorter: columnSorter('total_losses'),
    render: (text) => (text ? formatNumber(text) : '0'),
  },
  {
    title: 'Conversion to Paid',
    dataIndex: 'conversion_to_paid',
    key: 'conversion_to_paid',
    sorter: columnSorter('conversion_to_paid'),
    render: (text) => (text ? formatNumber(text) : '0'),
  },
  {
    title: 'Conversion Rate (%)',
    dataIndex: 'conversion_rate',
    key: 'conversion_rate',
    render: (text) => (text ? text : '0'),
    sorter: columnSorter('conversion_rate'),
  },
];

const detailCols = [
  {
    title: 'No.',
    dataIndex: 'id',
    key: 'id',
    sorter: columnSorter('id'),
  },
  {
    title: 'Phone Number',
    dataIndex: 'isdn',
    key: 'isdn',
    sorter: columnSorter('isdn'),
    render: (text) => (text ? text : '-'),
  },
  {
    title: 'Time Play',
    dataIndex: 'start_time',
    key: 'start_time',
    render: (date) => (date ? formatDate(date) : '-'),
    sorter: columnSorter('start_time'),
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    render: (result) =>
      getActiveBadge(result === 1, {
        activeText: 'Win',
        inactiveText: 'Lose',
      }),
    sorter: columnSorter('result'),
  },
  {
    title: 'Win/Play',
    dataIndex: 'win_play_week',
    key: 'win_play_week',
    sorter: columnSorter('win_play'),
    render: (text) => (text ? text : '-'),
  },
];

const START_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

const filterOptions = [
  { label: 'All', value: null },
  { label: 'Win', value: 1 },
  { label: 'Lose', value: 2 },
];

function FreePlay() {
  const [query, setQuery] = useState({
    isdn: null,
    result: null,
  });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const {
    data: dataDetails,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getFreePlay({
        ...query,
        start_date: date[0],
        end_date: date[1],
        limit: pageSize,
        offset: offset,
      }),
    ['free-play', query, date, pageSize, offset]
  );
  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    refetch: refetchOverview,
    isRefetching: isOverviewRefetching,
  } = usePaginatedQuery(
    () =>
      getFreePlayOverview({
        start_date: date[0],
        end_date: date[1],
      }),
    ['free-play-overview', date]
  );

  const handleQuery = () => {
    setQuery({
      isdn: searchValue,
      result: selectedFilter,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setDate([START_DATE, END_DATE]);
    setQuery({
      isdn: null,
      result: null,
      start_date: START_DATE,
      end_date: END_DATE,
    });
    setPageIndex(1);
  };

  const reversedOverviewData = useMemo(
    () => (overviewData ? [...overviewData].reverse() : []),
    [overviewData]
  );

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Free Play History" />

      <AppTableControl
        hasSelect={false}
        date={date}
        setDate={setDate}
        setPageIndex={setPageIndex}
        noStyle
        dateLimit={{
          value: 30,
          unit: 'day',
        }}
      />

      <AppTable
        columns={overviewCols}
        dataSource={reversedOverviewData}
        hasPagination={false}
        scroll={{ x: 992 }}
        loading={isOverviewLoading || isOverviewRefetching}
        onRefresh={refetchOverview}
        disableColumnVisibility={true}
        summary={(pageData) => {
          const summaryCols = [
            { key: 'total_play', label: 'Total Plays' },
            { key: 'total_win', label: 'Wins' },
            { key: 'total_losses', label: 'Loses' },
            { key: 'conversion_to_paid', label: 'Conversion to Paid' },
            { key: 'conversion_rate', label: 'Conversion Rate (%)' },
          ];

          const aggregatedData = pageData.reduce(
            (acc, item) => {
              acc.total_play += item.total_play || 0;
              acc.total_win += item.total_win || 0;
              acc.total_losses += item.total_losses || 0;
              acc.conversion_to_paid += item.conversion_to_paid || 0;
              return acc;
            },
            {
              total: 0,
              total_play: 0,
              total_win: 0,
              total_losses: 0,
              conversion_to_paid: 0,
              conversion_rate: '0.00%',
            }
          );

          aggregatedData.conversion_rate =
            aggregatedData.total_play > 0
              ? ((aggregatedData.conversion_to_paid / aggregatedData.total_play) * 100).toFixed(2) +
                '%'
              : '0%';

          if (!aggregatedData) {
            return null;
          }

          const values = summaryCols.map((col) => aggregatedData[col.key]);

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} align="center" className="font-semibold">
                  Total
                </Table.Summary.Cell>
                {values.map((value, index) => (
                  <Table.Summary.Cell
                    key={index + 1}
                    index={index + 1}
                    align="center"
                    className="font-semibold"
                  >
                    {typeof value === 'number' ? formatNumber(value) : value}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />

      <AppTableQuery
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        searchPlaceholder="Phone number"
        // hasFilter={false}
        filterPlaceholder="Result"
        filterLabel="Result"
        filterOptions={filterOptions}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        handleQuery={handleQuery}
        handleReset={handleReset}
        className="mt-2"
      />

      <AppTable
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(size) => {
          setPageSize(size);
        }}
        columns={detailCols}
        dataSource={dataDetails?.data}
        totalItems={dataDetails?.total}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
      />
    </Space>
  );
}

export default FreePlay;
